import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CaseStatusType } from '@viewer/data/case-status.data';
import { CommonRequestService } from './common-request.service';
import {
  EventOutcomeLogType,
  EventTypeLogType,
  IClaimantDetailsDto,
} from './project-actions.data';
import { IClaimantEditDetailsDto } from './project-open-action.data';
import { JsonResponse } from './json-response';
import { ROLE } from '@shared/helpers/constants';
import { Router } from '@angular/router';

@Injectable()
export class ProjectActionsRequestService extends CommonRequestService {
  constructor(private _http: HttpClient, private router: Router) {
    super();
  }

  /**
   * PUT: Update status of case
   * @method requestUpdateCaseStatus
   * @param {string} caseId Case ID
   * @param {CaseStatusType} caseStatus Case status
   * @param {string} errorNotes notes for reject case
   */
  requestUpdateCaseStatus(
    caseId: string,
    isApproved: CaseStatusType,
    errorNotes: string | undefined = undefined
  ): Observable<any> {
    return this._http.put(`${this._url}/updatecasestatus`, {
      caseId: caseId,
      isApproved: isApproved,
      errorNotes: errorNotes,
    });
  }

  /**
   * PUT: Reject status of case
   * @method requestRejectCaseStatus
   * @param {string} caseId Case ID
   * @param {string} errorNotes notes for reject case
   */
  requestRejectCaseStatus(
    caseId: string,
    errorNotes: string,
    claimantUserName: string | undefined,
    claimantFirstName: string | undefined,
    claimantLastName: string | undefined
  ): Observable<any> {
    return this._http.put(`${this._url}/rejectcase`, {
      caseStatus: 11,
      caseId: caseId,
      errorNotes: errorNotes,
      claimantUserName: claimantUserName,
      claimantFirstName: claimantFirstName,
      claimantLastName: claimantLastName,
    });
  }

  /**
   * GET: Get log from backend
   * @method requestGetLogForCase
   * @param {string} caseId Case ID
   * @return {string} log from backend for current case
   */
  requestGetLogForCase(caseId: string): Observable<any> {
    return this._http.get(`${this._urlCaseLogger}/exportlog`, {
      params: {
        caseId: caseId,
      },
      responseType: 'text' as 'json',
    });
  }

  /**
   * POST: Post log to backend
   * @method requestSaveLogForCase
   * @param {string} caseId Case ID
   * @param {string} userName mail of user
   * @param {string} action action of event
   * @param {string} dateTime date time of event
   * @param {string} eventOutcome
   * @param {string} eventType
   */
  requestSaveLogForCase(
    caseId: string,
    userName: string,
    action: string,
    dateTime: string,
    eventOutCome: EventOutcomeLogType,
    eventType: EventTypeLogType
  ): Observable<any> {
    return this._http.post(`${this._urlCaseLogger}/auditlog`, {
      action: action,
      caseId: caseId,
      dateTime: dateTime,
      eventOutCome: eventOutCome,
      eventType: eventType,
      userName: userName,
    });
  }

  /**
   * GET: Get edit permission
   * @method requestGetEditPermission
   * @param {string} caseId Case ID
   * @return {boolean} edit permission for current case
   */
  requestGetEditPermission(caseId: string): Observable<any> {
    return this._http.get(`${this._url}/editpermission`, {
      params: {
        caseId: caseId,
        buttonAction:'view'
      },
    });
  }

  /**
   * POST: Send exit info on backend
   * @method requestExitFromCase
   * @param {string} caseId Case ID
   */
  requestExitFromCase(caseId: string): Observable<any> {
    return this._http.post(`${this._url}/exitfromcase`, {
      caseId: caseId,
    });
  }

  /**
   * GET: Get claimant details for selected case
   * @method requestGetClaimantDetailsForCase
   * @param {string} caseId Case ID
   */
  requestGetClaimantDetailsForCase(
    caseId: string
  ): Observable<IClaimantDetailsDto> {
    return this._http.get<IClaimantDetailsDto>(`${this._url}/claimantdetails`, {
      params: {
        caseId: caseId,
      },
    });
  }

  /**
   * PUT: Put review time in seconds
   * @method requestUpdateReviewTime
   * @param {string} caseId Case ID
   * @param {number} reviewTimeInseconds Review time in seconds
   */
  requestUpdateReviewTime(
    caseId: string,
    reviewTimeInseconds: number
  ): Observable<any> {
    console.log('review time api request payload::caseId: ', caseId, 'reviewTimeInseconds: ', reviewTimeInseconds);
    return this._http.put(`${this._url}/reviewtime`, {
      caseId: caseId,
      reviewTimeInseconds: reviewTimeInseconds,
    });
  }

   /**
   * get: fetch edited flag status of case
   * @method requestUpdateCaseStatus
   * @param {string} caseId Case ID
   */
   requestUpdateEditCaseDetails(
    caseId: string
  ): Observable<IClaimantEditDetailsDto> {
    return this._http.get<IClaimantEditDetailsDto>(`${this._url}/editcasedetails`, {
      params: {
        caseId: caseId,
      },
    });
  }

  /**
 * PUT: Update status of edit case
 * @method requestUpdateEditCaseStatus
 * @param {string} caseId Case ID
 * @param {number} editFlag Case edit Flag
 * @param {string} claimantSavedUserName Case edit save username
 */
  requestUpdateEditCaseStatus(
    caseId: string,
    editFlag: number,
    claimantSavedUserName: string,
  ): Observable<any> {
    return this._http.put(`${this._url}/updateeditcaseetails`, {
      caseId: caseId,
      editFlag: editFlag,
      claimantSavedUserName: claimantSavedUserName,
    });
  }

  validateHash(caseId : string) : Observable<JsonResponse> { 
    return this._http.get<JsonResponse>(`${this._url}/validatechecksum`, {
       params: {
        caseId,
      },
    });
  }

  redirectToCaseList(): void {
    const CHOOSE_ROUTE = JSON.parse(
      localStorage.getItem('userDetails') || '{}'
    ).userRole === ROLE.SNN_HIL_MANAGER ? 'activecase' : 'mycases';
    this.router.navigate([`/${CHOOSE_ROUTE}`]);
  }
}
